import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Folder, CalendarClock, AlertTriangle, Loader2, FileText, ChevronRight } from "lucide-react";

export default function CustomerDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [dash, setDash] = useState(null);
  const [folders, setFolders] = useState(null);

  useEffect(() => {
    api.get("/dashboard/customer").then((r) => setDash(r.data));
    if (user?.company_id) api.get(`/companies/${user.company_id}/folders`).then((r) => setFolders(r.data));
  }, [user]);

  if (!dash || !folders) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-emerald-600" /></div>;

  return (
    <div className="fade-in space-y-8">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-[#0a192f]">Belgelerim</h1>
        <p className="mt-1 text-slate-500">{user?.company_name} · yayımlanan belgeler ve yaklaşan son tarihler</p>
      </div>

      <section>
        <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-[#0a192f]">
          <CalendarClock className="h-5 w-5 text-amber-600" /> Yaklaşan ve Süresi Dolan Belgeler
        </h2>
        {dash.rows.length === 0 ? (
          <Card className="flex items-center gap-3 p-5 text-slate-500" data-testid="customer-deadlines-empty">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><CalendarClock className="h-5 w-5" /></div>
            Şu anda takip gerektiren yaklaşan veya süresi dolmuş belgeniz bulunmuyor.
          </Card>
        ) : (
          <div className="space-y-2">
            {dash.rows.map((r) => (
              <Card key={r.record_id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`customer-deadline-${r.record_id}`}>
                <div className="flex items-center gap-3">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-full ${r.state === "expired" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-[#0a192f]">{r.name}{r.subtype ? <span className="ml-1 text-xs text-slate-400">({r.subtype})</span> : ""}</div>
                    <div className="text-xs text-slate-500">{r.type_label} · Son tarih: {r.deadline}</div>
                  </div>
                </div>
                <StatusBadge status={{ state: r.state, days_left: r.days_left }} />
              </Card>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-[#0a192f]">
          <Folder className="h-5 w-5 text-emerald-600" /> Yayımlanan Belge Klasörleri
        </h2>
        {folders.length === 0 ? (
          <Card className="p-8 text-center text-slate-400" data-testid="customer-folders-empty">
            <FileText className="mx-auto mb-2 h-8 w-8 text-slate-300" />
            Henüz yayımlanmış belge bulunmuyor.
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {folders.map((f) => (
              <Card key={f.index} onClick={() => nav(`/musteri/klasor/${f.index}`)} data-testid={`customer-folder-${f.index}`}
                className="group cursor-pointer p-5 transition-all hover:border-emerald-300 hover:shadow-md">
                <div className="flex items-start justify-between">
                  <Folder className="h-6 w-6 text-emerald-600" />
                  <ChevronRight className="h-5 w-5 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-emerald-600" />
                </div>
                <h3 className="mt-3 text-sm font-semibold text-[#0a192f]">{f.title}</h3>
                <p className="mt-1 text-xs text-slate-400">{f.published_count} belge</p>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
